import { useRoute, Link } from "wouter";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Loader2, Settings, Calendar, BarChart3, FileText, Printer } from "lucide-react";
import { useProject } from "@/hooks/useProjects";
import { useQuery } from "@tanstack/react-query";
import { ManagementView } from "@/components/projects/ManagementView";
import { CalendarView } from "@/components/projects/CalendarView";
import { GanttView } from "@/components/projects/GanttView";
import { ReportView } from "@/components/projects/ReportView";

export default function ProjectDetail() {
  const [, params] = useRoute("/projects/:id");
  const { data: project, isLoading, isError } = useProject(params?.id);

  // Fetch companies for client linking
  const { data: companies = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["companies-list"],
    queryFn: async () => {
      const res = await fetch("/api/companies?limit=500", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  const clientName = project?.companyId ? companies.find(c => c.id === project.companyId)?.name : null;

  if (isLoading) {
    return (
      <AppLayout title="Project">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !project) {
    return (
      <AppLayout title="Project">
        <div className="p-6 text-center py-12">
          <p className="text-muted-foreground">Project not found.</p>
          <Link href="/projects">
            <Button variant="outline" className="mt-4">Back to Projects</Button>
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={project.name}>
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link href="/projects" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold">{project.name}</h1>
              <p className="text-sm text-muted-foreground">
                Started {new Date(project.startDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                {clientName ? ` · ${clientName}` : ""}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              // Open the print page in a new tab. The print page
              // auto-triggers window.print() once the calendar and Gantt
              // have rendered, so the user gets the native print dialog
              // (with "Save as PDF" as a destination) without an extra
              // click. Opening in a new tab keeps the original project
              // page intact in case the user wants to come back.
              if (project) window.open(`/projects/${project.id}/print`, "_blank");
            }}
            data-testid="project-print-pdf"
          >
            <Printer className="w-4 h-4" />
            Print to PDF
          </Button>
        </div>

        <Tabs defaultValue="calendar" className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="calendar" className="gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="gantt" className="gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" />
              Gantt
            </TabsTrigger>
            <TabsTrigger value="management" className="gap-1.5">
              <Settings className="w-3.5 h-3.5" />
              Manage
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="mt-4">
            <CalendarView project={project} />
          </TabsContent>
          <TabsContent value="gantt" className="mt-4">
            <GanttView project={project} />
          </TabsContent>
          <TabsContent value="management" className="mt-4">
            <ManagementView project={project} companies={companies} />
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
